import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import '../styles/QRScanner.css';

export interface ScanResult {
  qr_code: string;  // QRコードの生データ
  action_type: 'check_in' | 'check_out';
  timestamp?: string;
}

interface QRScannerProps {
  /** スキャン完了時のコールバック */
  onScan: (result: ScanResult) => Promise<boolean>;
  /** スキャナーが有効かどうか */
  isActive: boolean;
  /** カメラの向き: 'environment'（背面） or 'user'（前面） */
  facingMode?: 'environment' | 'user';
  /** スキャナーをクリアする時間（ミリ秒）。0で自動クリアなし */
  autoClearTime?: number;
  /** 開発用モック: 指定時はカメラをスキップしてアクション選択画面へ直接遷移 */
  mockQrCode?: string;
  /** アクション自動選択: 指定時はQR読取後にアクション選択をスキップして自動処理 */
  autoAction?: 'check_in' | 'check_out';
}

// スキャン状態
type ScannerState = 'scanning' | 'action_selection' | 'processing' | 'complete' | 'error';

const QRScanner: React.FC<QRScannerProps> = ({
  onScan,
  isActive,
  facingMode = 'environment',
  autoClearTime = 3000,
  mockQrCode,
  autoAction,
}) => {
  const scannerRef = useRef<HTMLDivElement>(null);
  const qrScannerRef = useRef<Html5Qrcode | null>(null);
  
  // カメラエラー
  const [cameraError, setCameraError] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState<boolean>(false);
  
  // スキャン状態管理
  const [scannerState, setScannerState] = useState<ScannerState>('scanning');
  const [scannedQRCode, setScannedQRCode] = useState<string>('');
  const [selectedAction, setSelectedAction] = useState<'check_in' | 'check_out' | null>(null);

  // 開発用モック: mockQrCode が指定されていればカメラをスキップしてアクション選択へ
  useEffect(() => {
    if (mockQrCode && isActive && scannerState === 'scanning') {
      setScannedQRCode(mockQrCode);
      setScannerState('action_selection');
    }
  }, [mockQrCode, isActive, scannerState]);

  // autoAction: QR読取後にアクション選択をスキップして自動処理
  useEffect(() => {
    if (autoAction && scannedQRCode && scannerState === 'action_selection') {
      handleActionSelect(autoAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAction, scannedQRCode, scannerState]);

  // QRコードスキャナの初期化と管理
  useEffect(() => {
    if (!isActive || !scannerRef.current) {
      return;
    }

    // モックモード時はカメラを起動しない
    if (mockQrCode) {
      return;
    }

    // 操作選択状態の場合はスキャナーを停止
    if (scannerState !== 'scanning') {
      return;
    }

    const initializeScanner = async () => {
      try {
        setIsInitializing(true);
        setCameraError('');

        const html5QrCode = new Html5Qrcode('qr-reader');
        qrScannerRef.current = html5QrCode;

        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        };

        const onSuccess = (decodedText: string) => {
          setScannedQRCode(decodedText);
          setScannerState('action_selection');
          html5QrCode.pause();
        };

        const onError = (error: string) => {
          console.debug('QRスキャンエラー:', error);
        };

        // PCでは背面カメラがないためフロントカメラへフォールバック
        try {
          await html5QrCode.start({ facingMode: facingMode }, config, onSuccess, onError);
        } catch {
          await html5QrCode.start({ facingMode: 'user' }, config, onSuccess, onError);
        }
      } catch (err: any) {
        console.error('QRスキャナ初期化エラー:', err);

        // html5-qrcode は Error ではなく文字列を reject することがあり、その場合
        // err.name / err.message が両方 undefined になる（＝「…に失敗しました: undefined」
        // と表示され、権限拒否の判定にも入らない）。まず文字列に正規化してから判定する。
        const detail = (typeof err === 'string' ? err : `${err?.name ?? ''} ${err?.message ?? ''}`).trim();

        if (detail.includes('NotAllowedError') || detail.includes('Permission denied')) {
          setCameraError('カメラへのアクセス許可が拒否されました。ブラウザの設定を確認してください。');
        } else if (detail.includes('NotFoundError') || detail.includes('No suitable camera')) {
          setCameraError('カメラが見つかりません。デバイスにカメラが接続されていることを確認してください。');
        } else if (detail.includes('NotSupportedError')) {
          setCameraError('このブラウザはQRコードスキャンに対応していません。別のブラウザを試してください。');
        } else {
          // 原文はコンソールにのみ残し、画面には内部情報を出さない固定文言を表示する
          setCameraError('カメラを起動できませんでした。ブラウザの設定でカメラを許可し、画面を開き直してください。');
        }
      } finally {
        setIsInitializing(false);
      }
    };

    initializeScanner();

    return () => {
      const scanner = qrScannerRef.current;
      if (scanner) {
        const state = scanner.getState();
        if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
          scanner.stop().then(() => {
            scanner.clear();
          }).catch((err: any) => {
            console.warn('スキャナー停止エラー:', err);
          });
        }
      }
    };
  }, [isActive, scannerState, facingMode, mockQrCode]);

  // 操作選択ボタンのハンドラ
  const handleActionSelect = async (action: 'check_in' | 'check_out') => {
    setScannerState('processing');
    setSelectedAction(action);

    try {
      const result: ScanResult = {
        qr_code: scannedQRCode,
        action_type: action,
        timestamp: new Date().toISOString()
      };

      // 親コンポーネントのコールバック実行（API完了まで待機）
      const isSuccess = await onScan(result);

      if (isSuccess) {
        setScannerState('complete');

        // オート完了タイマー: autoClearTime後に状態をリセット
        // scannerStateが'scanning'に戻るとuseEffectが再実行されスキャナーが自動再起動する
        if (autoClearTime > 0) {
          setTimeout(() => {
            setScannedQRCode('');
            setSelectedAction(null);
            setScannerState('scanning');
          }, autoClearTime);
        }
      } else {
        setScannedQRCode('');
        setSelectedAction(null);
        setScannerState('scanning');
      }
    } catch (error) {
      console.error('操作選択処理エラー:', error);
      setScannerState('error');
    }
  };

  // 操作取消（scannerStateを'scanning'に戻すとuseEffectがスキャナーを再起動する）
  const handleCancel = () => {
    setScannedQRCode('');
    setSelectedAction(null);
    setScannerState('scanning');
  };

  return (
    <div className="qr-scanner-wrapper">
      {/* エラー表示 */}
      {cameraError && (
        <div className="scanner-error">
          <p className="error-icon">⚠️</p>
          <p className="error-message">{cameraError}</p>
        </div>
      )}

      {/* スキャナーコンテナ（スキャン状態のみ表示） */}
      {!cameraError && scannerState === 'scanning' && (
        <>
          <div className="scanner-container">
            {isInitializing && (
              <div className="scanner-initializing">
                <div className="spinner"></div>
                <p>カメラを初期化中...</p>
              </div>
            )}
            <div ref={scannerRef} id="qr-reader"></div>
          </div>
          <p className="scan-instruction">QRコードをスキャンしてください</p>
        </>
      )}

      {/* 操作選択画面（QRスキャン後、autoAction未指定時のみ表示） */}
      {scannerState === 'action_selection' && !autoAction && (
        <div className="action-selection-panel">
          <div className="scanned-qr-info">
            <p className="qr-code-preview">{scannedQRCode}</p>
            <p className="selection-prompt">
              出勤または退勤を選択してください
            </p>
          </div>

          <div className="action-buttons">
            <button
              className="action-btn check-in-btn"
              onClick={() => handleActionSelect('check_in')}
              disabled={scannerState !== 'action_selection'}
            >
              <span className="btn-label">出勤</span>
              <span className="btn-desc">出勤</span>
            </button>

            <button
              className="action-btn check-out-btn"
              onClick={() => handleActionSelect('check_out')}
              disabled={scannerState !== 'action_selection'}
            >
              <span className="btn-label">退勤</span>
              <span className="btn-desc">退勤</span>
            </button>
          </div>

          <button
            className="cancel-btn"
            onClick={handleCancel}
            disabled={scannerState !== 'action_selection'}
          >
            QRを読み込みなおす
          </button>
        </div>
      )}

      {/* 処理中画面 */}
      {(scannerState === 'processing' || (scannerState === 'action_selection' && autoAction)) && (
        <div className="processing-panel">
          <div className="spinner"></div>
          <p>
            {(selectedAction || autoAction) === 'check_in' ? '出勤' : '退勤'}を
            処理中...
          </p>
        </div>
      )}

      {/* 完了画面 */}
      {scannerState === 'complete' && (
        <div className="complete-panel">
          <div className="success-checkmark">✅</div>
          <p>
            {selectedAction === 'check_in'
              ? '出勤しました'
              : '退勤しました'}
          </p>
          <p className="complete-message">
            {selectedAction === 'check_in'
              ? '退勤時も忘れずにQRコードを読み取ってください。'
              : 'マネージャーの確認後、勤怠が承認されます。'}
          </p>
        </div>
      )}
    </div>
  );
};

export default QRScanner;
